import { DeliveryItemImage } from '../../components/atomic/delivery_items/DeliveryItemImage';
import type { GiantIconName } from '../../components/atomic/giants/giant_icons/GiantIcon';
import { getDeliveryCardGiantLayout } from '../../components/atomic/giants/giant_icons/deliveryCardGiantLayouts';
import { GIANT_BADGE_ICONS } from '../../components/atomic/giants/giant_icons/giantIconAssets';
import '../../components/atomic/giants/giant_icons/GiantIcon.css';
import { GIANT_IDS } from '../../../../domain/ids/ids';
import { DeliveryRating } from '../delivery_rating/DeliveryRating';
import { FailedDelivery } from '../failed_delivery/FailedDelivery';
import {
  DELIVERY_CARD_BACKGROUND_URL,
  DELIVERY_CARD_BUTTON_AGAIN_URL,
  DELIVERY_CARD_BUTTON_MORE_DETAILS_URL,
  DELIVERY_CARD_CLOSED_FRAME_URL,
  DELIVERY_CARD_DECOR_LABEL_URL,
  DELIVERY_CARD_GIANT_NAME_TEXT_URL,
  DELIVERY_CARD_LEVEL_NAME_TEXT_URL,
} from './deliveryCardAssets';
import './DeliveryCard.css';

export type DeliveryCardItemFrame = 'opened' | 'closed';

export type DeliveryCardProps = {
  /** Id гиганта для иконки в плашке. */
  giantId?: GiantIconName;
  /** Индекс заказа гиганта (0-based). */
  orderIndex?: number;
  /** Кадр предмета внутри плашки: открытый фон или закрытая рамка. */
  itemFrame?: DeliveryCardItemFrame;
  levelNameText?: string;
  giantNameText?: string;
  statusBanner?: 'rating' | 'failed';
  deliveryRating?: number | null;
  onAgain?: () => void;
  onMoreDetails?: () => void;
  againDisabled?: boolean;
  /** Activate delivered item (e.g. vinyl music toggle). */
  onItemActivate?: () => void;
};

/**
 * Карточка доставки в списке (new-delivery-result-card).
 * Plaque: 111 541 859×940.
 */
export function DeliveryCard({
  giantId = 'roza',
  orderIndex = 0,
  itemFrame = 'closed',
  levelNameText,
  giantNameText,
  statusBanner = 'failed',
  deliveryRating = null,
  onAgain,
  onMoreDetails,
  againDisabled = false,
  onItemActivate,
}: DeliveryCardProps) {
  const giantLayout = getDeliveryCardGiantLayout(giantId);
  const ariaLabel = [levelNameText, giantNameText].filter(Boolean).join(', ') || undefined;

  return (
    <div className="delivery-card" aria-label={ariaLabel}>
      <img
        className="delivery-card__background"
        src={DELIVERY_CARD_BACKGROUND_URL}
        alt=""
        draggable={false}
      />
      {GIANT_IDS.map((id) => {
        const isSelected = id === giantId;
        return (
          <img
            key={id}
            className={[
              'giant-icon',
              'delivery-card__giant',
              isSelected ? null : 'giant-icon--hidden',
            ]
              .filter(Boolean)
              .join(' ')}
            style={{
              left: giantLayout.left,
              top: giantLayout.top,
              width: giantLayout.width,
              height: giantLayout.height,
            }}
            src={GIANT_BADGE_ICONS[id]}
            alt=""
            draggable={false}
            aria-hidden={!isSelected}
          />
        );
      })}
      {itemFrame === 'closed' ? (
        <img
          className="delivery-card__item"
          src={DELIVERY_CARD_CLOSED_FRAME_URL}
          alt=""
          draggable={false}
        />
      ) : null}
      <DeliveryItemImage
        giantId={giantId}
        orderIndex={orderIndex}
        placement="delivery-card"
        hidden={itemFrame !== 'opened'}
        onActivate={
          itemFrame === 'opened' ? onItemActivate : undefined
        }
      />
      {levelNameText ? (
        <svg
          className="delivery-card__level-name delivery-card__level-name--live"
          width={606}
          height={64}
          viewBox="0 0 606 64"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden
        >
          <text
            className="delivery-card__level-name-text"
            textAnchor="middle"
            xmlSpace="preserve"
          >
            <tspan x={303} y={52}>
              {levelNameText}
            </tspan>
          </text>
        </svg>
      ) : (
        <img
          className="delivery-card__level-name"
          src={DELIVERY_CARD_LEVEL_NAME_TEXT_URL}
          alt="Спиночесалка"
          draggable={false}
        />
      )}
      {giantNameText ? (
        <svg
          className="delivery-card__giant-name delivery-card__giant-name--live"
          width={276}
          height={38}
          viewBox="0 0 276 38"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden
        >
          <text
            className="delivery-card__giant-name-text"
            textAnchor="middle"
            xmlSpace="preserve"
          >
            <tspan x={138} y={31}>
              {giantNameText}
            </tspan>
          </text>
        </svg>
      ) : (
        <img
          className="delivery-card__giant-name"
          src={DELIVERY_CARD_GIANT_NAME_TEXT_URL}
          alt="Обсерватория Роза"
          draggable={false}
        />
      )}
      <img
        className="delivery-card__decor-label"
        src={DELIVERY_CARD_DECOR_LABEL_URL}
        alt="Что внутри"
        draggable={false}
      />
      {statusBanner === 'failed' ? (
        <FailedDelivery />
      ) : (
        <DeliveryRating rating={deliveryRating} />
      )}
      <button
        type="button"
        className="delivery-card__button delivery-card__button--again"
        aria-label="Заново"
        onClick={onAgain}
        disabled={againDisabled || onAgain == null}
      >
        <img
          className="delivery-card__button-img"
          src={DELIVERY_CARD_BUTTON_AGAIN_URL}
          alt=""
          draggable={false}
        />
      </button>
      <button
        type="button"
        className="delivery-card__button delivery-card__button--details"
        aria-label="Подробнее"
        onClick={onMoreDetails}
        disabled={onMoreDetails == null}
      >
        <img
          className="delivery-card__button-img"
          src={DELIVERY_CARD_BUTTON_MORE_DETAILS_URL}
          alt=""
          draggable={false}
        />
      </button>
    </div>
  );
}
